import { useCallback, useState, type ReactNode } from "react";
import { Xmark, Heart, Calendar, MapPin, Person } from "@gravity-ui/icons";
import type { Event } from "../../reducers/apiTypes";
import { EventImage } from "../common/EventImage";
import { EventTag } from "../common/EventTag";
import {
    categoryColor,
    categoryLabel,
    formatEventScheduleLong,
} from "../../utils/eventDisplay";

type Props = {
    event: Event | null;
    onClose: () => void;
};

export function EventDetailModal({ event, onClose }: Props) {
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const handleShare = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setShareDialogOpen(true);
        } catch {
            setShareDialogOpen(true);
        }
    }, []);

    if (!event) return null;

    const color = categoryColor(event.event_type);
    const eventLink = event.link?.trim();

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-end justify-center bg-backdrop p-0 sm:items-center sm:p-4"
                role="dialog"
                aria-modal="true"
                aria-label={event.name}
                onClick={onClose}
            >
                <div
                    className="relative flex max-h-[95vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-[var(--overlay-shadow)] sm:rounded-[2rem]"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="relative aspect-[16/9] shrink-0 sm:aspect-[2/1]">
                        <EventImage
                            event={event}
                            className="h-full w-full rounded-none"
                            label="EVENT HERO PHOTO"
                        />
                        <div className="absolute right-3 top-3 flex gap-2 sm:right-4 sm:top-4">
                            <button
                                type="button"
                                className="flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-sm font-medium text-foreground shadow-sm backdrop-blur-sm"
                            >
                                <Heart className="size-4" />
                                Save
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex size-9 items-center justify-center rounded-full bg-white/95 text-foreground shadow-sm backdrop-blur-sm"
                                aria-label="Close"
                            >
                                <Xmark className="size-5" />
                            </button>
                        </div>
                        <div className="absolute bottom-3 left-3 flex gap-2 sm:bottom-4 sm:left-4">
                            <EventTag label={categoryLabel(event.event_type)} color={color} />
                            <EventTag label="Free" variant="free" />
                        </div>
                    </div>

                    <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-6 sm:p-8">
                        <h2 className="text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                            {event.name}
                        </h2>

                        <div className="flex flex-col gap-4">
                            <InfoRow
                                icon={<Calendar className="size-5" />}
                                label="When"
                                primary={formatEventScheduleLong(event)}
                            />
                            <InfoRow
                                icon={<MapPin className="size-5" />}
                                label="Where"
                                primary={event.address?.split(",")[0] ?? event.address ?? "—"}
                                secondary={event.address}
                            />
                            <InfoRow
                                icon={<Person className="size-5" />}
                                label="Hosted by"
                                primary={event.scraped_source ?? "Community organizer"}
                                secondary="Community organizer"
                            />
                        </div>

                        {event.description && (
                            <div
                                className="text-sm leading-relaxed text-foreground/80 [&_a]:text-accent-orange [&_p]:mb-2"
                                dangerouslySetInnerHTML={{ __html: event.description }}
                            />
                        )}
                    </div>

                    <div className="flex shrink-0 items-center justify-between gap-4 border-t border-border px-6 py-4 sm:px-8 sm:py-5">
                        <p className="text-sm text-muted">
                            <span className="font-bold text-foreground">Free</span> · RSVP
                            appreciated
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={handleShare}
                                className="rounded-full border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-surface-secondary"
                            >
                                Share
                            </button>
                            <button
                                type="button"
                                className="rounded-full bg-accent-orange px-5 py-2 text-sm font-semibold text-white transition hover:opacity-90"
                                onClick={() => {
                                    if (eventLink)
                                        window.open(eventLink, "_blank", "noopener,noreferrer");
                                }}
                            >
                                {eventLink ? "View event" : "RSVP – count me in"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {shareDialogOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-backdrop p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Link copied"
                    onClick={() => setShareDialogOpen(false)}
                >
                    <div
                        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-[var(--overlay-shadow)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-lg font-bold text-foreground">
                            Link copied!
                        </h3>
                        <p className="mt-2 text-sm text-muted">
                            The event link has been copied to your clipboard. Share it
                            with friends so they can view this event.
                        </p>
                        <p className="mt-3 break-all rounded-xl bg-surface-secondary px-3 py-2 text-xs text-foreground">
                            {window.location.href}
                        </p>
                        <button
                            type="button"
                            onClick={() => setShareDialogOpen(false)}
                            className="mt-5 w-full rounded-full bg-accent-orange py-3 text-sm font-semibold text-white transition hover:opacity-90"
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

function InfoRow({
    icon,
    label,
    primary,
    secondary,
}: {
    icon: ReactNode;
    label: string;
    primary: string;
    secondary?: string;
}) {
    return (
        <div className="flex gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-surface-secondary text-muted">
                {icon}
            </div>
            <div className="min-w-0">
                <p className="text-xs text-muted">{label}</p>
                <p className="font-semibold text-foreground">{primary}</p>
                {secondary && secondary !== primary && (
                    <p className="text-xs text-muted">{secondary}</p>
                )}
            </div>
        </div>
    );
}
